import { useEffect, useState, type ReactNode } from "react";
import type { NotificationItem } from "./notifications";
import { Sidebar, type AccountSummary } from "./sidebar";
import { TopBar } from "./top-bar";

/**
 * The signed-in portal frame: fixed nav rail from `lg` up, off-canvas drawer
 * below it, sticky top bar, and one `<main>` the screens render into.
 *
 * Everything it displays arrives as a prop. The design read the account and the
 * notification list from a fixture module; this shell is handed them by the
 * route that owns the session, which is what keeps `src/ui/` free of data.
 */
export function AppShell({
  account,
  suiteId,
  registrationState,
  registrationTone,
  notifications = [],
  pendingCount = 0,
  onSignOut,
  children,
}: {
  account: AccountSummary;
  suiteId: string;
  registrationState: string;
  registrationTone?: "success" | "warning" | "danger" | "brand" | "neutral";
  notifications?: NotificationItem[];
  pendingCount?: number;
  onSignOut?: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  // Navigation closes the drawer via SidebarContent's onNavigate; Escape closes it here.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Lock scroll while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-100 focus:rounded-field focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:shadow-raised"
      >
        Skip to content
      </a>

      <Sidebar
        account={account}
        open={open}
        onClose={() => setOpen(false)}
        badges={{ pendingDevices: pendingCount }}
        onSignOut={onSignOut}
      />

      <div className="lg:pl-sidebar">
        <TopBar
          initials={account.initials}
          suiteId={suiteId}
          registrationState={registrationState}
          registrationTone={registrationTone}
          notifications={notifications}
          onMenu={() => setOpen(true)}
        />
        <main id="main" className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 sm:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}
